// Page-geometry engine — built from the render engine's OWN layout library
// (synced from assets/photobook_page_layouts.json by scripts/sync-layouts.mjs).
// This is the single source of truth for slot rects, so a web page reproduces
// the printed page's geometry exactly.
import rawLayouts from "./photobook_page_layouts.json";

type Rect = [x: number, y: number, w: number, h: number]; // mm, trim-origin

interface RawSlot {
  index: number;
  rect: Rect;
  bleed?: boolean;
}
interface RawLayout {
  id: string;
  name: string;
  group: string;
  capacity: number;
  background: string;
  slots: RawSlot[];
}
interface RawFile {
  page: {
    trimMm: number;
    bleedMm: number;
    fullCanvasMm: number;
  };
  layouts: RawLayout[];
}

interface RawFileWithMeta extends RawFile {
  meta?: { version?: string };
}
const file = rawLayouts as unknown as RawFileWithMeta;

/** Version of the synced layout library — pin against the manifest's
 *  `layoutLibraryVersion` so geometry can't silently drift from print. */
export const LIBRARY_VERSION = file.meta?.version ?? "unknown";

/** The web page IS the trimmed sheet: bleed is physically cut off the book,
 *  so the reader viewport is the trim square and clips bleed via overflow. */
export const TRIM_MM = file.page.trimMm; // 210

/** A slot rect expressed as fractions of the trim square, y from top.
 *  left/top may be negative and width/height may exceed 1 for full-bleed
 *  slots — the page container clips them (overflow:hidden), reproducing the
 *  physical trim. */
export interface SlotFrac {
  index: number;
  left: number;
  top: number;
  width: number;
  height: number;
  bleed: boolean;
}

export interface Layout {
  id: string;
  name: string;
  capacity: number;
  /** Page background colour from the layout (e.g. cinema is #111). */
  background: string;
  slots: SlotFrac[];
}

function toFrac([x, y, w, h]: Rect): Omit<SlotFrac, "index" | "bleed"> {
  return {
    left: x / TRIM_MM,
    top: y / TRIM_MM,
    width: w / TRIM_MM,
    height: h / TRIM_MM,
  };
}

const LAYOUTS: Record<string, Layout> = Object.fromEntries(
  file.layouts.map((l) => [
    l.id,
    {
      id: l.id,
      name: l.name,
      capacity: l.capacity,
      background: l.background,
      slots: l.slots.map((s) => ({
        index: s.index,
        bleed: Boolean(s.bleed),
        ...toFrac(s.rect),
      })),
    },
  ]),
);

export function getLayout(id: string): Layout | undefined {
  return LAYOUTS[id];
}

export function allLayoutIds(): string[] {
  return Object.keys(LAYOUTS);
}
